// Pure placement rules for the custom <select> replacement (BentoSelect).
//
// The legacy dashboard replaces every native <select> with a custom picker: a
// fixed popover on desktop and for the global top-bar selects, and an inline
// accordion for a select that lives inside a placed module on mobile. Two of
// those decisions — which mode to open, and where to put the popover — are pure
// geometry with no DOM or React in them, so they live here and are unit-tested
// once. BentoSelect (app-web) owns the actual rendering; it asks these
// functions what to draw.
//
// Faithful to the legacy behaviour in main.js (the "Dropdown picker" block):
//   mode  = mobile-viewport AND inside a .placed-module → inline, else popover
//   place = popover flush under the select, left-aligned, at least as wide as
//           it, nudged left only if it would overflow the right edge.

/** Which custom UI a select opens. */
export type PickerMode = "inline" | "popover";

/**
 * Inline accordion only when BOTH hold: the viewport is mobile AND the select
 * lives inside a placed module. Every other case — desktop at any width, or a
 * global top-bar select on any viewport — is the fixed popover. Mirrors the
 * legacy `isMobile() && sel.closest('.placed-module')` branch exactly.
 */
export function selectPickerMode(
  isMobileViewport: boolean,
  insidePlacedModule: boolean
): PickerMode {
  return isMobileViewport && insidePlacedModule ? "inline" : "popover";
}

/** The parts of a DOMRect the popover placement needs. */
export interface AnchorRect {
  left: number;
  bottom: number;
  width: number;
}

/** Absolute (position: fixed) placement for the popover. */
export interface PopoverPlacement {
  left: number;
  top: number;
  minWidth: number;
}

/** Gap in px between the select's bottom edge and the popover's top. */
export const POPOVER_GAP = 2;
/** Keep-clear margin from the viewport edges before nudging the popover left. */
export const POPOVER_MARGIN = 8;

/**
 * Initial placement: flush under the select, left edges aligned, at least as
 * wide as the select. Mirrors the legacy `left/top/min-width` set from the
 * select's rect.
 */
export function popoverPlacement(rect: AnchorRect): PopoverPlacement {
  return {
    left: rect.left,
    top: rect.bottom + POPOVER_GAP,
    minWidth: rect.width,
  };
}

/**
 * Overflow correction, run once the popover's real width is known: if it would
 * spill past the right edge (minus the margin), pull it left — but never past
 * the left margin. Mirrors the legacy rAF clamp. Returns the corrected `left`
 * (unchanged when it already fits).
 */
export function clampPopoverLeft(
  left: number,
  popoverWidth: number,
  viewportWidth: number,
  margin: number = POPOVER_MARGIN
): number {
  if (left + popoverWidth > viewportWidth - margin) {
    return Math.max(margin, viewportWidth - popoverWidth - margin);
  }
  return left;
}
